import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { InfoPageLayout } from "@/components/pages/InfoPageLayout";
import { policyPageSchema, breadcrumbSchema, ldScripts } from "@/lib/seo/schema";

const TITLE = "Terms & Conditions - Indonesian Calendar";
const DESC = "Terms and conditions for using Indonesian Calendar, including content license, limitation of liability, and holiday data disclaimer.";

export const Route = createFileRoute("/en/terms")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Terms & Conditions" },
      { property: "og:description", content: "Rules for using Indonesian Calendar." },
    ],
    links: [{ rel: "canonical", href: "https://hari.co.id/en/terms/" }],
    scripts: ldScripts([
      policyPageSchema({ url: "/en/terms", name: TITLE, description: DESC, kind: "TermsOfService", locale: "en", dateModified: "2026-05-01" }),
      breadcrumbSchema([
        { name: "Home", url: "/en" },
        { name: "Terms & Conditions", url: "/en/terms" },
      ]),
    ]),
  }),
  component: TermsPageEn,
});

const TOC = [
  { id: "use", label: "Use of the site" },
  { id: "accuracy", label: "Data accuracy" },
  { id: "license", label: "Copyright & license" },
  { id: "liability", label: "Limitation of liability" },
  { id: "external", label: "External links" },
  { id: "changes", label: "Changes" },
];

function TermsPageEn() {
  return (
    <SiteShell locale="en">
      <InfoPageLayout
        variant="legal"
        title="Terms & Conditions"
        subtitle="Rules for using the site and data disclaimer."
        badge="T"
        lastUpdated="May 1, 2026"
        locale="en"
        breadcrumbs={[{ label: "Home", href: "/en" }, { label: "Terms & Conditions" }]}
        aside={
          <div className="rounded-xl border border-border bg-card p-4 text-sm">
            <h3 className="mb-2 font-semibold">Contents</h3>
            <ol className="space-y-1.5 text-muted-foreground">
              {TOC.map((t, i) => (
                <li key={t.id}>
                  <a className="hover:text-primary" href={`#${t.id}`}>{i + 1}. {t.label}</a>
                </li>
              ))}
            </ol>
          </div>
        }
      >
        <p>
          By accessing and using <strong>Indonesian Calendar</strong>, you agree
          to the following terms.
        </p>

        <h2 id="use">1. Use of the site</h2>
        <ul>
          <li>This site is provided for general date-reference purposes.</li>
          <li>You may not use the site for unlawful activity, abusive scraping that overloads our servers, or other misuse.</li>
        </ul>

        <h2 id="accuracy">2. Data accuracy</h2>
        <p>
          Public holiday and joint leave data come from the SKB 3 Menteri.
          Hijri, Javanese, and Balinese conversions use arithmetic methods
          that may differ by 1 day from official observation. For official
          purposes please verify with authoritative sources.
        </p>

        <h2 id="license">3. Copyright &amp; license</h2>
        <p>
          Text, design, and layout on this site are owned by Indonesian
          Calendar. Calendar dates themselves are public information. You may
          copy holiday dates for personal or internal organizational use, but
          you may <strong>not</strong> copy entire pages to other sites without
          attribution.
        </p>

        <h2 id="liability">4. Limitation of liability</h2>
        <p>
          We are not responsible for any losses arising from use of data on
          this site, including but not limited to scheduling errors, travel
          planning, or business decisions.
        </p>

        <h2 id="external">5. External links</h2>
        <p>
          The site may contain third-party links. We are not responsible for
          third-party content or privacy practices.
        </p>

        <h2 id="changes">6. Changes</h2>
        <p>
          Terms may be updated at any time. See also{" "}
          <Link to={"/en/privacy-policy" as never}>Privacy Policy</Link> and{" "}
          <Link to={"/en/contact" as never}>Contact</Link>.
        </p>
      </InfoPageLayout>
    </SiteShell>
  );
}